import React, { useState, useMemo } from "react"
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  MenuItem,
  Divider,
  Fade,
  IconButton,
  Tooltip,
  Drawer,
  Avatar,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Menu,
  Collapse,
  Popover,
} from "@mui/material"
import ViewInArIcon from "@mui/icons-material/ViewInAr"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import LogoutIcon from "@mui/icons-material/Logout"
import SaveIcon from "@mui/icons-material/Save"
import DescriptionIcon from "@mui/icons-material/Description"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import ExtensionIcon from "@mui/icons-material/Extension"
import StarIcon from "@mui/icons-material/Star"
import KitchenIcon from "@mui/icons-material/Kitchen"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import FactoryIcon from "@mui/icons-material/Factory"
import PersonIcon from "@mui/icons-material/Person"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import RemoveIcon from "@mui/icons-material/Remove"
import LockIcon from "@mui/icons-material/Lock"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useStoreLogic } from "../useStoreLogic"
import { useCpqV5State } from "./useCpqV5State"
import { useCategoryOptions } from "./useCategoryOptions"
import { splitSwatches } from "constants/catalogData"
import {
  WallCabinetsSvg,
  BaseCabinetsSvg,
  TallCabinetsSvg,
  CountertopSvg,
  WallSectionIcon,
  BaseSectionIcon,
  TallSectionIcon,
} from "./KitchenSvgIllustrations"
import BoxDiagram from "../BoxDiagram"
import AddBoxDialog from "./AddBoxDialog"
import TextSummaryPanel from "./TextSummaryPanel"
import EnhancedSummaryPanel from "./EnhancedSummaryPanel"
import KitchenPreviewModal from "./KitchenPreviewModal"
import { useQuoteHistory } from "./useQuoteHistory"
import QuoteHistoryDrawer from "./QuoteHistoryDrawer"
import QuoteGatePage from "./QuoteGatePage"

// ─── Eliton Brand Colors ──────────────────────────────────────────
const ELITON = {
  red: "#e50019",
  redDark: "#c40016",
  dark: "#212121",
  darkLight: "#2c2c2c",
  white: "#ffffff",
  gray: "#f4f4f4",
  grayMed: "#858585",
  grayBorder: "#ebebeb",
}

// ─── Eliton Logo SVG (white + red variant) ────────────────────────
const ElitonLogo = ({ height = 24 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={height * (114.882 / 34.923)} height={height} viewBox="0 0 114.882 34.923">
    <defs><clipPath id="eliton-clip"><rect width="114.882" height="34.923" fill="none" /></clipPath></defs>
    <g transform="translate(0 0)">
      <rect width="3.844" height="26.376" transform="translate(41.399 7.835)" fill="#fff" />
      <g clipPath="url(#eliton-clip)">
        <path d="M66.085,3.8l-.1-.584L65.7,2.681l-.438-.438L64.772,2l-.633-.1-.584.1-.536.243-.389.438-.292.535-.1.584.1.584A1.91,1.91,0,0,0,65.7,4.92l.292-.535Z" transform="translate(-20.843 -0.637)" fill="#fff" />
        <rect width="3.796" height="34.211" transform="translate(31.909)" fill="#fff" />
        <path d="M22.857,22.815q-.219-.656-.438-1.314a9.706,9.706,0,0,0-17.812,0l-.486,1.314Zm-18.979,3.8c1.478,8.576,11.592,11.457,17.13,4.964l.779-1.168h4.283a24.9,24.9,0,0,1-1.606,2.871A13.516,13.516,0,0,1,.082,23.788q.146-.8.292-1.606C4.394,7.887,24.3,8.659,26.9,23.4a18.568,18.568,0,0,1,.049,3.212H3.878" transform="translate(0 -3.982)" fill="#fff" />
        <path d="M80.378,26.133l.1.925A4.874,4.874,0,0,0,85.1,31.049l.925-.1V34.8l-1.168.049A8.77,8.77,0,0,1,76.631,27.2l-.049-1.07V0h3.8V8.273h5.645V12.02H80.378Z" transform="translate(-25.646 0)" fill="#fff" />
        <path d="M115.157,20.182l-.827-1.119a9.737,9.737,0,0,0-17.082,7.251l.243,1.363-3.552,1.508L93.6,27.579a13.52,13.52,0,0,1,24.187-10.268l.876,1.363Z" transform="translate(-31.289 -3.963)" fill={ELITON.red} />
        <path d="M117.6,28.946l.146,1.363a9.739,9.739,0,0,1-16.692,7.056l-.876-1.022L96.63,37.852l.973,1.314a13.509,13.509,0,0,0,23.846-10.122l-.292-1.606Z" transform="translate(-32.359 -9.189)" fill="#fff" />
        <path d="M143.668,23.266l.1-1.119a7.119,7.119,0,0,1,14.064,0l.1,1.119V38.353h3.8V23.266L161.673,22c-2.1-13.495-21.115-12.534-21.8,1.12V38.353h3.8Z" transform="translate(-46.84 -4.141)" fill="#fff" />
      </g>
    </g>
  </svg>
)

// ─── Countertop color options with visual swatches ──────────────
const PAGKOS_COLORS = [
  { value: "Λευκο",        label: "Λευκο",        color: "#f5f5f4", border: "#d6d3d1" },
  { value: "Λευκο Γυαλιστ", label: "Λευκο Γυαλιστ.", color: "#fafaf9", border: "#d6d3d1", gloss: true },
  { value: "Γκρι Ανοιχτο", label: "Γκρι Ανοιχτο", color: "#d1d5db" },
  { value: "Γκρι Σκουρο",  label: "Γκρι Σκουρο",  color: "#6b7280" },
  { value: "Ανθρακι",      label: "Ανθρακι",      color: "#374151" },
  { value: "Μαυρο",        label: "Μαυρο",        color: "#1c1917" },
  { value: "Μαυρο Γυαλιστ", label: "Μαυρο Γυαλιστ.", color: "#0c0a09", gloss: true },
  { value: "Δρυς Ανοιχτο", label: "Δρυς Ανοιχτο", color: "#d4a76a" },
  { value: "Δρυς Σκουρο",  label: "Δρυς Σκουρο",  color: "#92600a" },
  { value: "Καρυδια",      label: "Καρυδια",      color: "#78350f" },
  { value: "Μαρμαρο Λευκο", label: "Μαρμαρο Λευκο", color: "#f5f5f4", pattern: "marble-w" },
  { value: "Μαρμαρο Γκρι", label: "Μαρμαρο Γκρι", color: "#d1d5db", pattern: "marble-g" },
  { value: "Γρανιτης",     label: "Γρανιτης",     color: "#57534e", pattern: "granite" },
  { value: "Μπετον",       label: "Μπετον",       color: "#a8a29e" },
]


// ─── Kitchen Container Section ──────────────────────────────────
const SECTION_CONFIG = {
  Wall: { label: "ΚΡΕΜΑΣΤΑ", color: "#2563eb", bgColor: "#eff6ff", borderColor: "#bfdbfe", Icon: WallSectionIcon, BigSvg: WallCabinetsSvg },
  Base: { label: "ΒΑΣΕΩΣ", color: "#16a34a", bgColor: "#f0fdf4", borderColor: "#bbf7d0", Icon: BaseSectionIcon, BigSvg: BaseCabinetsSvg },
  Tall: { label: "ΟΛΟΣΩΜΑ", color: "#7c3aed", bgColor: "#f5f3ff", borderColor: "#ddd6fe", Icon: TallSectionIcon, BigSvg: TallCabinetsSvg },
}

const parseCategories = (cat) => {
  if (!cat) return []
  try {
    const parsed = JSON.parse(cat)
    return Array.isArray(parsed) ? parsed : [cat]
  } catch {
    return [cat]
  }
}

const getItemPosition = (item) => {
  if (item.cabinet_position) {
    const map = { BASE: "Base", WALL: "Wall", TALL: "Tall" }
    return map[item.cabinet_position.toUpperCase()] || null
  }
  const cats = parseCategories(item.category)
  if (cats.includes("Base") || cats.includes("ΒΑΣΕΩΣ")) return "Base"
  if (cats.includes("Wall") || cats.includes("ΚΡΕΜΑΣΤΟ")) return "Wall"
  if (cats.includes("Tall") || cats.includes("ΟΛΟΣΩΜΟ")) return "Tall"
  return null
}

const MiniCabinetCard = ({ item, onRemove, onUpdateQty, calculateFinalPrice, swatchesByGroup, onUpdateSwatch, onEditItem, t }) => {
  const [swatchAnchor, setSwatchAnchor] = useState(null)
  // Try customDimensions first, then product fields, then first variant as last resort
  let pW = item.customDimensions?.length || (item.productWidth ? parseInt(item.productWidth) : 0)
  let pH = item.customDimensions?.height || (item.productHeight ? parseInt(item.productHeight) : 0)
  let pD = item.productDepth ? parseInt(item.productDepth) : 0
  // Fallback: extract from the first matching variant if dimensions are still 0
  if ((!pW || !pH) && item.variants) {
    try {
      const variants = JSON.parse(item.variants)
      const v = Array.isArray(variants) && variants.length > 0
        ? variants.find(v => v.sku === item.sku) || variants[0]
        : null
      if (v) {
        if (!pW && v.width) pW = parseInt(v.width)
        if (!pH && v.height) pH = parseInt(v.height)
        if (!pD && v.depth) pD = parseInt(v.depth)
      }
    } catch { /* ignore */ }
  }
  const swatchUrl = item.swatch?.swatchUrl
  const unitPrice = parseFloat(item.b2bsalesprice) || 0
  // Mini-card swatch picker shows colors only — door-model is locked once chosen.
  const availableSwatches = useMemo(() => {
    if (!item.is_configurable || !item.modelCode) return []
    const flat = swatchesByGroup?.[item.modelCode] || []
    return splitSwatches(flat).colors
  }, [item.is_configurable, item.modelCode, swatchesByGroup])

  return (
    <Paper
      elevation={0}
      onClick={() => onEditItem && onEditItem(item)}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 1.5,
        p: 1,
        minWidth: 100,
        maxWidth: 120,
        position: "relative",
        cursor: onEditItem ? "pointer" : "default",
        transition: "all 0.15s",
        "&:hover": { borderColor: "#e50019", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
        "&:hover .remove-btn": { opacity: 1 },
      }}
    >
      {/* Remove button */}
      <IconButton
        className="remove-btn"
        size="small"
        onClick={(e) => { e.stopPropagation(); onRemove(item.cartItemKey) }}
        sx={{
          position: "absolute", top: -8, right: -8,
          width: 20, height: 20,
          bgcolor: "#ef4444", color: "#fff",
          opacity: 0,
          transition: "opacity 0.15s",
          "&:hover": { bgcolor: "#dc2626" },
        }}
      >
        <CloseIcon sx={{ fontSize: 12 }} />
      </IconButton>

      {/* 3D Box SVG */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 0.5 }}>
        <BoxDiagram
          length={pW}
          height={pH}
          width={pD}
          size={72}
          swatchUrl={swatchUrl}
          productName={item.name}
        />
      </Box>

      {/* Swatch picker button */}
      {availableSwatches.length > 0 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0.25 }}>
            <Tooltip title={item.swatch?.label || t("Χρωμα")} arrow placement="top">
              <Box
                onClick={(e) => { e.stopPropagation(); setSwatchAnchor(e.currentTarget) }}
                sx={{
                  width: 18, height: 18, borderRadius: "50%",
                  border: "2px solid #e2e8f0",
                  cursor: "pointer",
                  backgroundImage: swatchUrl ? `url(${swatchUrl})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  bgcolor: swatchUrl ? "transparent" : "#d1d5db",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: "#e50019", transform: "scale(1.15)" },
                }}
              />
            </Tooltip>
          </Box>
          <Popover
            open={Boolean(swatchAnchor)}
            anchorEl={swatchAnchor}
            onClose={() => setSwatchAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{ paper: { sx: { p: 1, maxWidth: 180, borderRadius: 2 } } }}
          >
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#64748b", mb: 0.5, textAlign: "center" }}>
              {t("Χρωμα")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
              {availableSwatches.map((sw) => {
                const isSelected = item.swatch?.label === sw.label
                return (
                  <Tooltip key={sw.label} title={sw.label} arrow placement="top">
                    <Box
                      onClick={() => {
                        if (!isSelected && onUpdateSwatch) onUpdateSwatch(item.cartItemKey, sw)
                        setSwatchAnchor(null)
                      }}
                      sx={{
                        width: 24, height: 24, borderRadius: "50%",
                        backgroundImage: sw.swatchUrl ? `url(${sw.swatchUrl})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        bgcolor: sw.swatchUrl ? "transparent" : "#d1d5db",
                        border: isSelected ? "2.5px solid #e50019" : "1.5px solid #e2e8f0",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        "&:hover": { transform: "scale(1.15)", borderColor: "#e50019" },
                      }}
                    />
                  </Tooltip>
                )
              })}
            </Box>
          </Popover>
        </>
      )}

      {/* Name */}
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 600,
          color: "#1e293b",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
        title={item.name}
      >
        {item.name}
      </Typography>

      {/* Dimensions */}
      {(pW > 0 || pH > 0) && (
        <Typography sx={{ fontSize: "0.55rem", color: "#64748b", mt: 0.25, textAlign: "center" }}>
          {pW > 0 ? pW : "?"}×{pH > 0 ? pH : "?"}
        </Typography>
      )}

      {/* Custom size flag */}
      {item.customSizeSurcharge > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25 }}>
          <Chip
            label={`Custom +€${item.customSizeSurcharge}`}
            size="small"
            sx={{
              height: 16, fontSize: "0.5rem", fontWeight: 700,
              bgcolor: "#fef3c7", color: "#d97706", borderColor: "#fcd34d",
              border: "1px solid",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* RAL custom-color flag */}
      {item.customRalCode && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25 }}>
          <Chip
            label={`RAL ${item.customRalCode}`}
            size="small"
            sx={{
              height: 16, fontSize: "0.5rem", fontWeight: 700,
              bgcolor: "#fef3c7", color: "#92400e", borderColor: "#f59e0b",
              border: "1px solid",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* Door model flag */}
      {item.doorModel?.label && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25 }}>
          <Chip
            label={item.doorModel.label}
            size="small"
            sx={{
              height: 16, fontSize: "0.5rem", fontWeight: 700,
              bgcolor: "#cffafe", color: "#0891b2", borderColor: "#67e8f9",
              border: "1px solid",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* Door option flag */}
      {item.doorOption && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25 }}>
          <Chip
            label={item.doorOption}
            size="small"
            sx={{
              height: 16, fontSize: "0.5rem", fontWeight: 700,
              bgcolor: "#dbeafe", color: "#1d4ed8", borderColor: "#93c5fd",
              border: "1px solid",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* Κρεμαση flag */}
      {item.hingeSurcharge > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25 }}>
          <Chip
            label={`Κρεμαση ${item.hingeCm}cm +€${item.hingeSurcharge}`}
            size="small"
            sx={{
              height: 16, fontSize: "0.5rem", fontWeight: 700,
              bgcolor: "#ede9fe", color: "#7c3aed", borderColor: "#c4b5fd",
              border: "1px solid",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      )}

      {/* Unit price */}
      {unitPrice > 0 && (
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#3b82f6", mt: 0.25, textAlign: "center" }}>
          €{unitPrice.toFixed(2)}
        </Typography>
      )}

      {/* Unit info (Meter / CubicM) */}
      {item.unit === "Meter" && item.unitQuantity > 0 && (
        <Typography sx={{ fontSize: "0.5rem", color: "#92400e", fontWeight: 600, textAlign: "center", mt: 0.15 }}>
          {item.unitQuantity.toFixed(2)}m @ €{item.unitRate?.toFixed(2)}/m
        </Typography>
      )}
      {item.unit === "CubicM" && item.unitQuantity > 0 && (
        <Typography sx={{ fontSize: "0.5rem", color: "#92400e", fontWeight: 600, textAlign: "center", mt: 0.15 }}>
          {item.unitQuantity.toFixed(4)}m³
        </Typography>
      )}

      {/* Quantity stepper */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0} sx={{ mt: 0.5 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            if (item.quantity <= 1) {
              onRemove(item.cartItemKey)
            } else {
              onUpdateQty(item.cartItemKey, -1)
            }
          }}
          sx={{
            width: 22, height: 22,
            border: "1px solid #e2e8f0",
            borderRadius: 0.75,
            "&:hover": { borderColor: "#ef4444", color: "#ef4444" },
          }}
        >
          <RemoveIcon sx={{ fontSize: 12 }} />
        </IconButton>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#1e293b", minWidth: 20, textAlign: "center" }}>
          {item.quantity}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onUpdateQty(item.cartItemKey, 1) }}
          sx={{
            width: 22, height: 22,
            border: "1px solid #e2e8f0",
            borderRadius: 0.75,
            "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
          }}
        >
          <AddIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Stack>

    </Paper>
  )
}

const KitchenSection = ({ sectionKey, items, onAdd, onRemove, onUpdateQty, calculateFinalPrice, swatchUrl, countertopColor, swatchesByGroup, onUpdateSwatch, onEditItem, t }) => {
  const cfg = SECTION_CONFIG[sectionKey]
  if (!cfg) return null
  const SectionIcon = cfg.Icon
  const BigSvg = cfg.BigSvg

  const totalQty = items.reduce((s, i) => s + i.quantity, 0)
  const sectionSubtotal = items.reduce((s, i) => {
    const price = calculateFinalPrice ? calculateFinalPrice(parseFloat(i.b2bsalesprice) || 0) : 0
    return s + price * i.quantity
  }, 0)

  // Build swatch props for the big SVG based on section type
  const svgSwatchProps = useMemo(() => {
    if (sectionKey === "Wall") return { topSwatchUrl: swatchUrl, bottomSwatchUrl: null, countertopColor }
    if (sectionKey === "Base") return { topSwatchUrl: null, bottomSwatchUrl: swatchUrl, countertopColor }
    if (sectionKey === "Tall") return { tallSwatchUrl: swatchUrl, topSwatchUrl: null, bottomSwatchUrl: null, countertopColor }
    return {}
  }, [sectionKey, swatchUrl, countertopColor])

  return (
    <Box
      sx={{
        border: `1px solid ${cfg.borderColor}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header: icon + title + count + subtotal + add button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 0.75,
          bgcolor: cfg.bgColor,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {SectionIcon && <SectionIcon size={22} color={cfg.color} />}
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: cfg.color }}>
            {t(cfg.label)}
          </Typography>
          {totalQty > 0 && (
            <Chip
              label={`${totalQty} ${t("τεμ.")}`}
              size="small"
              sx={{
                height: 20, fontSize: "0.6rem", fontWeight: 700,
                bgcolor: `${cfg.color}18`, color: cfg.color,
              }}
            />
          )}
          {sectionSubtotal > 0 && (
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: cfg.color }}>
              €{sectionSubtotal.toFixed(2)}
            </Typography>
          )}
        </Stack>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => onAdd(sectionKey)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            color: cfg.color,
            borderColor: cfg.color,
            border: "1px solid",
            borderRadius: 1.5,
            px: 1.5,
            py: 0.25,
            "&:hover": { bgcolor: `${cfg.color}10` },
          }}
        >
          {t("Προσθηκη")}
        </Button>
      </Box>

      {/* Body: Big SVG illustration (left) + cabinet cards (right) */}
      <Box sx={{ display: "flex", px: 2, py: 1.5, minHeight: 100, gap: 2 }}>
        {/* Big section illustration */}
        {BigSvg && (
          <Box
            sx={{
              flexShrink: 0,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.85,
            }}
          >
            <BigSvg width={140} height={100} {...svgSwatchProps} />
          </Box>
        )}

        {/* Cabinet cards area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {items.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 80,
                border: "2px dashed #e2e8f0",
                borderRadius: 1.5,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { borderColor: cfg.color, bgcolor: cfg.bgColor },
              }}
              onClick={() => onAdd(sectionKey)}
            >
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                {t("Κλικ για προσθηκη")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {items.map((item) => (
                <MiniCabinetCard
                  key={item.cartItemKey}
                  item={item}
                  onRemove={onRemove}
                  onUpdateQty={onUpdateQty}
                  calculateFinalPrice={calculateFinalPrice}
                  swatchesByGroup={swatchesByGroup}
                  onUpdateSwatch={onUpdateSwatch}
                  onEditItem={onEditItem}
                  t={t}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ─── Accessories/Special compact chip card ─────────────────
const AccessoryChipCard = ({ item, onRemove, onUpdateQty, calculateFinalPrice, t }) => {
  const unitPrice = calculateFinalPrice ? calculateFinalPrice(parseFloat(item.b2bsalesprice) || 0) : 0
  return (
    <Chip
      label={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.name}
          </Typography>
          {item.unit === "Meter" && item.unitQuantity > 0 && (
            <Typography sx={{ fontSize: "0.55rem", color: "#92400e", fontWeight: 600 }}>{item.unitQuantity.toFixed(2)}m</Typography>
          )}
          {item.unit === "CubicM" && item.unitQuantity > 0 && (
            <Typography sx={{ fontSize: "0.55rem", color: "#92400e", fontWeight: 600 }}>{item.unitQuantity.toFixed(4)}m³</Typography>
          )}
          {item.customRalCode && (
            <Typography sx={{ fontSize: "0.55rem", color: "#92400e", fontWeight: 700, bgcolor: "#fef3c7", px: 0.5, borderRadius: 0.5 }}>
              RAL {item.customRalCode}
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.6rem", color: "#64748b" }}>×{item.quantity}</Typography>
          {unitPrice > 0 && (
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#3b82f6" }}>
              €{(unitPrice * item.quantity).toFixed(2)}
            </Typography>
          )}
        </Stack>
      }
      onDelete={() => onRemove(item.cartItemKey)}
      deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
      variant="outlined"
      sx={{
        height: 28,
        borderColor: "#e2e8f0",
        "&:hover": { borderColor: "#e50019" },
        "& .MuiChip-label": { px: 1 },
      }}
    />
  )
}

// ─── Building Phase ─────────────────────────────────────────
const BuildingPhase = ({ v5, storeLogic, categoryOptions, catLoading, t, quoteHistory }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [pdfAnchorEl, setPdfAnchorEl] = useState(null)
  const [accExpanded, setAccExpanded] = useState(false)
  const [specExpanded, setSpecExpanded] = useState(false)
  const [pagkosExpanded, setPagkosExpanded] = useState(false)
  const [doorCladExpanded, setDoorCladExpanded] = useState(false)

  // Open preview if triggered from config phase
  React.useEffect(() => {
    if (v5.compatBridge.openPreviewOnBuild) {
      setPreviewOpen(true)
      v5.compatBridge.setOpenPreviewOnBuild(false)
    }
  }, [v5.compatBridge.openPreviewOnBuild])

  // Build model options from DB categories
  const modelOptions = useMemo(() => {
    return (categoryOptions?.Model || []).map(opt => ({
      code: opt.value || opt.name,
      label: opt.description
        ? `${opt.value || opt.name} — ${opt.description}`
        : opt.name,
    }))
  }, [categoryOptions?.Model])

  // Build Kasoma options from DB categories
  const kasomaOptions = useMemo(() => {
    return (categoryOptions?.Kasoma || []).map(opt => opt.name)
  }, [categoryOptions?.Kasoma])


  // Group cart items by position (cabinets) and non-cabinet items
  const { sectionItems, accessoryItems, specialItems, pagkosItems, doorCladdingItems } = useMemo(() => {
    const sections = { Wall: [], Base: [], Tall: [] }
    const accessories = []
    const specials = []
    const pagkos = []
    const doorCladding = []
    storeLogic.cart.forEach((item) => {
      const pos = getItemPosition(item)
      if (pos && sections[pos]) {
        sections[pos].push(item)
      } else {
        // Check category for accessories vs special vs pagkos vs door-cladding
        let cats = []
        try { cats = JSON.parse(item.category || "[]") } catch { cats = [item.category] }
        if (cats.includes("ΠΟΡΤΑ ΕΠΕΝΔΥΣΗΣ") || cats.includes("Door")) {
          doorCladding.push(item)
        } else if (cats.includes("Pagkos")) {
          pagkos.push(item)
        } else if (cats.includes("Special") || cats.includes("Charges")) {
          specials.push(item)
        } else if (cats.includes("Accessories")) {
          accessories.push(item)
        } else {
          // Default non-positioned items to accessories
          accessories.push(item)
        }
      }
    })
    return {
      sectionItems: sections,
      accessoryItems: accessories,
      specialItems: specials,
      pagkosItems: pagkos,
      doorCladdingItems: doorCladding,
    }
  }, [storeLogic.cart])

  const handleKasomaSwitch = (newKasoma) => {
    v5.setKasoma(newKasoma)
    if (newKasoma && storeLogic.cart.length > 0) {
      storeLogic.switchCartKasoma(newKasoma)
    }
  }

  const handlePomoloSwitch = (newPomolo) => {
    v5.setPomolo(newPomolo)
    if (newPomolo && storeLogic.cart.length > 0) {
      storeLogic.switchCartPomolo(newPomolo)
    }
  }


  const handleAddProduct = (product, options) => {
    storeLogic.addToCart(product, options)
  }

  const handleEditItem = (item) => {
    const section = getItemPosition(item)
    v5.openAddBoxForItem(section, item.modelCode, item.swatch)
  }

  // Send for approval handler
  const handleSendForApproval = async () => {
    if (!quoteHistory?.activeQuoteId || !quoteHistory?.setQuoteStatus) return
    // Save current state first
    if (quoteHistory.saveQuote) await quoteHistory.saveQuote()
    await quoteHistory.setQuoteStatus(quoteHistory.activeQuoteId, "pending_approval")
  }

  // Final accept handler — user accepts the specialist's revision
  const handleFinalAccept = async () => {
    if (!quoteHistory?.activeQuoteId || !quoteHistory?.setQuoteStatus) return
    await quoteHistory.setQuoteStatus(quoteHistory.activeQuoteId, "final_accepted")
  }

  const hasNonCabinetItems = accessoryItems.length > 0 || specialItems.length > 0 || pagkosItems.length > 0

  // User must select Kasoma + Pomolo before seeing sections (Model is picked in the AddBox modal)
  const configReady = Boolean(v5.kasoma && v5.pomolo)

  return (
    <Fade in timeout={400}>
      <Box>
        {/* ── Compact Config Strip (single row) ── */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 0.75,
              bgcolor: "#fff",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Left group: Pomolo + Kasoma */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
              {/* Pomolo toggle chips */}
              <Stack direction="row" spacing={0.5}>
                {(categoryOptions?.Pomolo || []).map((opt, idx) => {
                  const active = v5.pomolo === opt.name
                  const chipColor = idx === 0 ? "#1d4ed8" : "#166534"
                  return (
                    <Chip
                      key={opt.name}
                      label={opt.name}
                      size="small"
                      variant={active ? "filled" : "outlined"}
                      onClick={() => handlePomoloSwitch(active ? "" : opt.name)}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: 26,
                        cursor: "pointer",
                        ...(active
                          ? { bgcolor: chipColor, color: "#fff", borderColor: "transparent" }
                          : { borderColor: "#d1d5db", color: "#6b7280" }),
                        "&:hover": active ? {} : { borderColor: chipColor, color: chipColor },
                      }}
                    />
                  )
                })}
              </Stack>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "#e2e8f0" }} />

              {/* Kasoma toggle chips */}
              <Stack direction="row" spacing={0.5}>
                {kasomaOptions.map(k => {
                  const active = v5.kasoma === k
                  return (
                    <Chip
                      key={k}
                      label={k}
                      size="small"
                      variant={active ? "filled" : "outlined"}
                      onClick={() => handleKasomaSwitch(active ? "" : k)}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        height: 26,
                        cursor: "pointer",
                        ...(active
                          ? { bgcolor: "#e50019", color: "#fff", borderColor: "transparent" }
                          : { borderColor: "#d1d5db", color: "#6b7280" }),
                        "&:hover": active ? {} : { borderColor: "#e50019", color: "#e50019" },
                      }}
                    />
                  )
                })}
              </Stack>
            </Stack>

            {/* Right group: Visual Editor + PDF export */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Button
                size="small"
                startIcon={<ViewInArIcon sx={{ fontSize: 16 }} />}
                onClick={() => setPreviewOpen(true)}
                disabled={storeLogic.cart.length === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  color: "#fff",
                  bgcolor: "#e50019",
                  border: "1px solid #e50019",
                  borderRadius: 1.5,
                  height: 32,
                  "&:hover": { bgcolor: "#c40016", borderColor: "#c40016" },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8", borderColor: "#e2e8f0" },
                }}
              >
                {t("Visual Editor")}
              </Button>
              <Button
                size="small"
                startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => setPdfAnchorEl(e.currentTarget)}
                disabled={storeLogic.cart.length === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  color: "#64748b",
                  borderColor: "#e2e8f0",
                  border: "1px solid",
                  borderRadius: 1.5,
                  height: 32,
                  "&:hover": { borderColor: "#e50019", color: "#e50019" },
                }}
              >
                PDF
              </Button>
            </Stack>
          </Box>

        </Paper>

        {/* PDF Export Menu */}
        <Menu
          anchorEl={pdfAnchorEl}
          open={Boolean(pdfAnchorEl)}
          onClose={() => setPdfAnchorEl(null)}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          <MenuItem
            onClick={() => { setPdfAnchorEl(null); storeLogic.handleExportManufacturer() }}
            sx={{ fontSize: "0.82rem" }}
          >
            <FactoryIcon sx={{ fontSize: 18, mr: 1, color: "#64748b" }} />
            {t("Κατασκευαστης")}
          </MenuItem>
          <MenuItem
            onClick={() => { setPdfAnchorEl(null); storeLogic.handleExportCustomer() }}
            disabled={quoteHistory?.activeQuote?.status !== "final_accepted"}
            sx={{ fontSize: "0.82rem" }}
          >
            {quoteHistory?.activeQuote?.status !== "final_accepted"
              ? <LockIcon sx={{ fontSize: 18, mr: 1, color: "#94a3b8" }} />
              : <PersonIcon sx={{ fontSize: 18, mr: 1, color: "#64748b" }} />
            }
            {t("Πελατης")}
            {quoteHistory?.activeQuote?.status !== "final_accepted" && (
              <Typography component="span" sx={{ fontSize: "0.6rem", ml: 0.5, color: "#94a3b8" }}>
                ({t("after accept")})
              </Typography>
            )}
          </MenuItem>
          <MenuItem
            onClick={() => { setPdfAnchorEl(null); storeLogic.handleExportSupplier() }}
            sx={{ fontSize: "0.82rem" }}
          >
            <LocalShippingIcon sx={{ fontSize: 18, mr: 1, color: "#64748b" }} />
            {t("Προμηθευτης")}
          </MenuItem>
        </Menu>

        {/* Main content: Kitchen containers + Smart sidebar */}
        {!configReady ? (
          /* ── Prompt to complete config ── */
          <Paper
            elevation={0}
            sx={{
              py: 6,
              px: 3,
              textAlign: "center",
              border: "2px dashed #e2e8f0",
              borderRadius: 3,
              bgcolor: "#fafbfc",
            }}
          >
            <KitchenIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569", mb: 1 }}>
              {t("Επιλεξτε Ρυθμισεις")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", maxWidth: 400, mx: "auto" }}>
              {t("Επιλεξτε Κασωμα και Pomolo απο την μπαρα ρυθμισεων για να ξεκινησετε την κατασκευη")}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
              {!v5.kasoma && (
                <Chip label={t("Κασωμα")} variant="outlined" sx={{ borderColor: "#e50019", color: "#e50019", fontWeight: 600 }} />
              )}
              {!v5.pomolo && (
                <Chip label={t("Pomolo")} variant="outlined" sx={{ borderColor: "#1d4ed8", color: "#1d4ed8", fontWeight: 600 }} />
              )}
            </Stack>
          </Paper>
        ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          {/* LEFT: Kitchen containers + Accessories/Special */}
          <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
            <Stack spacing={0}>
              {/* Wall cabinets (top) */}
              <KitchenSection
                sectionKey="Wall"
                items={sectionItems.Wall}
                onAdd={v5.openAddBoxForSection}
                onRemove={storeLogic.removeFromCart}
                onUpdateQty={storeLogic.updateQuantity}
                calculateFinalPrice={storeLogic.calculateFinalPrice}
                swatchUrl={v5.swatch?.swatchUrl || null}
                countertopColor={PAGKOS_COLORS.find(c => c.value === v5.pagkosColor)?.color}
                swatchesByGroup={v5.swatchesByGroup}
                onUpdateSwatch={storeLogic.updateItemSwatch}
                onEditItem={handleEditItem}
                t={t}
              />

              {/* ── PAGKOS (Countertop) Section ── */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  overflow: "hidden",
                  my: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 0.75,
                    bgcolor: "#fafafa",
                    cursor: "pointer",
                  }}
                  onClick={() => setPagkosExpanded(!pagkosExpanded)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CountertopSvg width={22} height={16} />
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#78350f" }}>
                      {t("ΠΑΓΚΟΣ")}
                    </Typography>
                    {pagkosItems.length > 0 && (
                      <Chip
                        label={pagkosItems.length}
                        size="small"
                        sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#78350f" }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => { e.stopPropagation(); v5.openAddBoxForSection("Pagkos") }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        color: "#78350f",
                        borderColor: "#78350f",
                        border: "1px solid",
                        borderRadius: 1.5,
                        px: 1,
                        py: 0.15,
                        height: 24,
                        "&:hover": { bgcolor: "#fef3c720" },
                      }}
                    >
                      {t("Add")}
                    </Button>
                    {pagkosExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                  </Stack>
                </Box>
                <Collapse in={pagkosExpanded}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    {pagkosItems.length === 0 ? (
                      <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
                        {t("Δεν εχουν προστεθει προιοντα παγκου")}
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {pagkosItems.map(item => (
                          <AccessoryChipCard
                            key={item.cartItemKey}
                            item={item}
                            onRemove={storeLogic.removeFromCart}
                            onUpdateQty={storeLogic.updateQuantity}
                            calculateFinalPrice={storeLogic.calculateFinalPrice}
                            t={t}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>

              {/* Base cabinets */}
              <KitchenSection
                sectionKey="Base"
                items={sectionItems.Base}
                onAdd={v5.openAddBoxForSection}
                onRemove={storeLogic.removeFromCart}
                onUpdateQty={storeLogic.updateQuantity}
                calculateFinalPrice={storeLogic.calculateFinalPrice}
                swatchUrl={v5.swatch?.swatchUrl || null}
                countertopColor={PAGKOS_COLORS.find(c => c.value === v5.pagkosColor)?.color}
                swatchesByGroup={v5.swatchesByGroup}
                onUpdateSwatch={storeLogic.updateItemSwatch}
                onEditItem={handleEditItem}
                t={t}
              />

              {/* Tall cabinets */}
              <Box sx={{ mt: 2 }}>
                <KitchenSection
                  sectionKey="Tall"
                  items={sectionItems.Tall}
                  onAdd={v5.openAddBoxForSection}
                  onRemove={storeLogic.removeFromCart}
                  onUpdateQty={storeLogic.updateQuantity}
                  calculateFinalPrice={storeLogic.calculateFinalPrice}
                  swatchUrl={v5.swatch?.swatchUrl || null}
                  countertopColor={PAGKOS_COLORS.find(c => c.value === v5.pagkosColor)?.color}
                  swatchesByGroup={v5.swatchesByGroup}
                  onUpdateSwatch={storeLogic.updateItemSwatch}
                  onEditItem={handleEditItem}
                  t={t}
                />
              </Box>
            </Stack>

            {/* ── ΕΙΔΙΚΑ (Special) Section ── */}
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 0.75,
                  bgcolor: "#fafafa",
                  cursor: "pointer",
                }}
                onClick={() => setSpecExpanded(!specExpanded)}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StarIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#475569" }}>
                    {t("ΕΙΔΙΚΑ")}
                  </Typography>
                  {specialItems.length > 0 && (
                    <Chip
                      label={specialItems.length}
                      size="small"
                      sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#f3e8ff", color: "#7c3aed" }}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); v5.openAddBoxForSection("Special") }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      color: "#7c3aed",
                      borderColor: "#7c3aed",
                      border: "1px solid",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.15,
                      height: 24,
                      "&:hover": { bgcolor: "#f5f3ff20" },
                    }}
                  >
                    {t("Add")}
                  </Button>
                  {specExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                </Stack>
              </Box>
              <Collapse in={specExpanded}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  {specialItems.length === 0 ? (
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
                      {t("Δεν εχουν προστεθει ειδικα")}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {specialItems.map(item => (
                        <AccessoryChipCard
                          key={item.cartItemKey}
                          item={item}
                          onRemove={storeLogic.removeFromCart}
                          onUpdateQty={storeLogic.updateQuantity}
                          calculateFinalPrice={storeLogic.calculateFinalPrice}
                          t={t}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>

            {/* ── ΠΟΡΤΕΣ ΕΠΕΝΔΥΣΗΣ (Door Cladding) Section ── */}
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 0.75,
                  bgcolor: "#fafafa",
                  cursor: "pointer",
                }}
                onClick={() => setDoorCladExpanded(!doorCladExpanded)}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <KitchenIcon sx={{ fontSize: 16, color: "#0891b2" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#475569" }}>
                    {t("ΠΟΡΤΕΣ ΕΠΕΝΔΥΣΗΣ")}
                  </Typography>
                  {doorCladdingItems.length > 0 && (
                    <Chip
                      label={doorCladdingItems.length}
                      size="small"
                      sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#cffafe", color: "#0891b2" }}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); v5.openAddBoxForSection("DoorCladding") }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      color: "#0891b2",
                      borderColor: "#0891b2",
                      border: "1px solid",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.15,
                      height: 24,
                      "&:hover": { bgcolor: "#ecfeff" },
                    }}
                  >
                    {t("Add")}
                  </Button>
                  {doorCladExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                </Stack>
              </Box>
              <Collapse in={doorCladExpanded}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  {doorCladdingItems.length === 0 ? (
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
                      {t("Δεν εχουν προστεθει πορτες επενδυσης")}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {doorCladdingItems.map(item => (
                        <AccessoryChipCard
                          key={item.cartItemKey}
                          item={item}
                          onRemove={storeLogic.removeFromCart}
                          onUpdateQty={storeLogic.updateQuantity}
                          calculateFinalPrice={storeLogic.calculateFinalPrice}
                          t={t}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>

            {/* ── ΑΞΕΣΟΥΑΡ (Accessories) Section ── */}
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 0.75,
                  bgcolor: "#fafafa",
                  cursor: "pointer",
                }}
                onClick={() => setAccExpanded(!accExpanded)}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ExtensionIcon sx={{ fontSize: 16, color: "#d97706" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#475569" }}>
                    {t("ΑΞΕΣΟΥΑΡ")}
                  </Typography>
                  {accessoryItems.length > 0 && (
                    <Chip
                      label={accessoryItems.length}
                      size="small"
                      sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#d97706" }}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); v5.openAddBoxForSection("Accessories") }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      color: "#d97706",
                      borderColor: "#d97706",
                      border: "1px solid",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.15,
                      height: 24,
                      "&:hover": { bgcolor: "#fef3c720" },
                    }}
                  >
                    {t("Add")}
                  </Button>
                  {accExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                </Stack>
              </Box>
              <Collapse in={accExpanded}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  {accessoryItems.length === 0 ? (
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
                      {t("Δεν εχουν προστεθει αξεσουαρ")}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {accessoryItems.map(item => (
                        <AccessoryChipCard
                          key={item.cartItemKey}
                          item={item}
                          onRemove={storeLogic.removeFromCart}
                          onUpdateQty={storeLogic.updateQuantity}
                          calculateFinalPrice={storeLogic.calculateFinalPrice}
                          t={t}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>

          </Box>

          {/* RIGHT: Enhanced Smart Sidebar */}
          {!isMobile && (
            <EnhancedSummaryPanel
              cart={storeLogic.cart}
              groupedCart={storeLogic.groupedCart}
              cartTotal={storeLogic.cartTotal}
              ralSurcharge={storeLogic.ralSurcharge}
              quoteDiscount={storeLogic.quoteDiscount}
              onQuoteDiscountChange={storeLogic.setQuoteDiscount}
              discountAmount={storeLogic.discountAmount}
              netAmount={storeLogic.netAmount}
              vatRate={storeLogic.vatRate}
              vatAmount={storeLogic.vatAmount}
              grandTotal={storeLogic.grandTotal}
              extraCharge={storeLogic.extraCharge}
              onExtraChargeChange={storeLogic.setExtraCharge}
              extraChargeAmount={storeLogic.extraChargeAmount}
              calculateFinalPrice={storeLogic.calculateFinalPrice}
              onUpdateQty={storeLogic.updateQuantity}
              onRemove={storeLogic.removeFromCart}
              settingsChips={v5.settingsChips}
              v5={v5}
              onExportManufacturer={storeLogic.handleExportManufacturer}
              onExportCustomer={storeLogic.handleExportCustomer}
              onExportSupplier={storeLogic.handleExportSupplier}
              onSendForApproval={quoteHistory?.activeQuoteId ? handleSendForApproval : undefined}
              onFinalAccept={quoteHistory?.activeQuote?.status === "revised" || quoteHistory?.activeQuote?.status === "approved" ? handleFinalAccept : undefined}
              quoteStatus={quoteHistory?.activeQuote?.status}
              reviewedBy={quoteHistory?.activeQuote?.reviewed_by}
              reviewedAt={quoteHistory?.activeQuote?.reviewed_at}
            />
          )}
        </Box>
        )}

        {/* Mobile: floating bar + drawer */}
        {isMobile && (
          <>
            <Paper
              elevation={4}
              sx={{
                position: "fixed",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 3,
                zIndex: 1100,
                bgcolor: "#fff",
              }}
            >
              <Chip
                label={`${storeLogic.cartItemCount} — €${storeLogic.cartTotal.toFixed(2)}`}
                size="small"
                sx={{ fontSize: "0.7rem", fontWeight: 700, bgcolor: "#fde8ea", color: "#9a0011" }}
              />
              <IconButton size="small" onClick={() => setRightDrawerOpen(true)} sx={{ color: "#e50019" }}>
                <ReceiptLongIcon />
              </IconButton>
            </Paper>
            <Drawer
              anchor="right"
              open={rightDrawerOpen}
              onClose={() => setRightDrawerOpen(false)}
              PaperProps={{ sx: { width: 320 } }}
            >
              <EnhancedSummaryPanel
                cart={storeLogic.cart}
                groupedCart={storeLogic.groupedCart}
                cartTotal={storeLogic.cartTotal}
                ralSurcharge={storeLogic.ralSurcharge}
                calculateFinalPrice={storeLogic.calculateFinalPrice}
                onUpdateQty={storeLogic.updateQuantity}
                onRemove={storeLogic.removeFromCart}
                settingsChips={v5.settingsChips}
                v5={v5}
                onExportManufacturer={storeLogic.handleExportManufacturer}
                onExportCustomer={storeLogic.handleExportCustomer}
                onExportSupplier={storeLogic.handleExportSupplier}
                onSendForApproval={quoteHistory?.activeQuoteId ? handleSendForApproval : undefined}
                onFinalAccept={quoteHistory?.activeQuote?.status === "revised" || quoteHistory?.activeQuote?.status === "approved" ? handleFinalAccept : undefined}
                quoteStatus={quoteHistory?.activeQuote?.status}
                reviewedBy={quoteHistory?.activeQuote?.reviewed_by}
                reviewedAt={quoteHistory?.activeQuote?.reviewed_at}
                profitMargin={storeLogic.profitMargin}
                onMarginChange={storeLogic.setProfitMargin}
              />
            </Drawer>
          </>
        )}

        {/* Add Box Dialog */}
        <AddBoxDialog
          open={v5.addBoxOpen}
          onClose={() => v5.setAddBoxOpen(false)}
          position={v5.addBoxSection}
          kasoma={v5.kasoma}
          priceGroup={v5.model || ""}
          swatch={v5.swatch || null}
          pomolo={v5.pomolo || ""}
          onAddProduct={handleAddProduct}
          productMode={v5.addBoxProductMode}
          modelOptions={modelOptions}
          swatchesByGroup={v5.swatchesByGroup}
          surcharges={v5.surcharges}
          initialModel={v5.addBoxInitialModel}
          initialSwatch={v5.addBoxInitialSwatch}
        />

        {/* Kitchen Preview Modal */}
        <KitchenPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          groupedCart={storeLogic.groupedCart}
          cpqState={v5.compatBridge}
          onUpdateQty={storeLogic.updateQuantity}
          onReorderCart={storeLogic.reorderCartItems}
          onAddCabinet={(section) => {
            setPreviewOpen(false)
            setTimeout(() => {
              if (section && v5.openAddBoxForSection) {
                v5.openAddBoxForSection(section)
              } else {
                v5.setAddBoxOpen(true)
              }
            }, 150)
          }}
        />
      </Box>
    </Fade>
  )
}

// ─── User Header Bar ─────────────────────────────────────────────
const QUOTE_STATUS_COLORS = {
  draft: { bg: "#475569", label: "Draft" },
  pending_approval: { bg: "#d97706", label: "Pending Approval" },
  approved: { bg: "#16a34a", label: "Approved" },
  changes_requested: { bg: "#dc2626", label: "Changes Requested" },
  revised: { bg: "#2563eb", label: "Revised by Specialist" },
  final_accepted: { bg: "#059669", label: "Final Accepted" },
}

const QuoteHeader = ({ user, onLogout, t, onOpenHistory, onSave, activeQuoteTitle, activeQuoteCustomer, quoteStatus }) => {
  const initials = useMemo(() => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase()
    }
    if (user.name) {
      const parts = user.name.split(" ")
      return parts.map(p => p[0]).join("").toUpperCase().slice(0, 2)
    }
    return (user.email || "U")[0].toUpperCase()
  }, [user])

  const displayName = user.first_name
    ? `${user.first_name} ${user.last_name || ""}`.trim()
    : user.name || user.email || ""

  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 1.5, sm: 2.5 },
        py: 0.75,
        bgcolor: ELITON.dark,
        borderBottom: `3px solid ${ELITON.red}`,
        borderRadius: 0,
        position: "sticky",
        top: 0,
        zIndex: 1200,
        minHeight: 52,
      }}
    >
      {/* Left: Eliton Logo */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <ElitonLogo height={22} />
        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 0.5 }} />
        <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.5)", fontWeight: 500, letterSpacing: 0.5 }}>
          Quote Composer
        </Typography>
      </Stack>

      {/* Center: Title */}
      <Box sx={{ position: "absolute", left: "50%", transform: "translateX(-50%)", textAlign: "center", display: { xs: "none", sm: "block" } }}>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>
            {activeQuoteTitle || t("Νεα Συνθεση")}
          </Typography>
          {quoteStatus && quoteStatus !== "draft" && (() => {
            const st = QUOTE_STATUS_COLORS[quoteStatus] || QUOTE_STATUS_COLORS.draft
            return (
              <Chip
                label={t(st.label)}
                size="small"
                sx={{ height: 18, fontSize: "0.55rem", fontWeight: 700, bgcolor: st.bg, color: "#fff" }}
              />
            )
          })()}
        </Stack>
        {activeQuoteCustomer ? (
          <Typography sx={{ fontSize: "0.6rem", color: ELITON.red, fontWeight: 600 }}>
            {activeQuoteCustomer}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.45)" }}>
            {t("Κατασκευη Κουζινας")}
          </Typography>
        )}
      </Box>

      {/* Right: Actions + User */}
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {onSave && (
          <Tooltip title={t("Save")}>
            <IconButton
              onClick={onSave}
              size="small"
              sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(229,0,25,0.2)" } }}
            >
              <SaveIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        {onOpenHistory && (
          <Tooltip title={t("Quotes")}>
            <IconButton
              onClick={onOpenHistory}
              size="small"
              sx={{ color: "rgba(255,255,255,0.65)", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
            >
              <DescriptionIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.12)", mx: 0.25 }} />
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: ELITON.red,
              fontSize: "0.7rem",
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#fff", lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.4)" }}>
              {user.email}
            </Typography>
          </Box>
        </Stack>
        <Tooltip title={t("Αποσυνδεση")}>
          <IconButton
            onClick={onLogout}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.5)",
              "&:hover": { color: "#fff", bgcolor: "rgba(229,0,25,0.3)" },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  )
}

// ─── Main V5 Component ───────────────────────────────────────────
const StorePageV5 = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const v5 = useCpqV5State()
  const storeLogic = useStoreLogic()
  const { categories: categoryOptions, loading: catLoading } = useCategoryOptions()

  // Gate state: whether user has selected/created a quote
  const [hasActiveQuote, setHasActiveQuote] = useState(false)

  // Quote history
  const quoteHistory = useQuoteHistory({ v5, storeLogic })

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser")) || {}
    } catch {
      return {}
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("authUser")
    navigate("/login")
  }

  const handleSave = () => {
    if (quoteHistory.activeQuoteId) {
      quoteHistory.saveQuote()
    } else {
      quoteHistory.setSaveDialogOpen(true)
    }
  }

  const enhancedStoreLogic = {
    ...storeLogic,
    handleExportCustomer: () => {
      storeLogic.handleExportCustomer({
        customerInfo: {
          name: quoteHistory.activeQuote?.customer_name || "",
          phone: quoteHistory.activeQuote?.customer_phone || "",
          email: quoteHistory.activeQuote?.customer_email || "",
          address: quoteHistory.activeQuote?.customer_address || "",
        },
        quoteTitle: quoteHistory.activeQuote?.title || "",
        settingsChips: v5.settingsChips,
      })
    },
  }

  // ── Gate: show QuoteGatePage if no active quote selected ──
  if (!hasActiveQuote) {
    return (
      <QuoteGatePage
        quotes={quoteHistory.quotes}
        loading={quoteHistory.loading}
        quoteHistory={quoteHistory}
        onQuoteReady={(quote) => {
          quoteHistory.loadQuote(quote)
          setHasActiveQuote(true)
        }}
        onNewQuote={(quote, margin) => {
          if (quote?.id) quoteHistory.setActiveQuoteId(quote.id)
          if (margin != null && margin > 0) storeLogic.setProfitMargin(margin)
          setHasActiveQuote(true)
        }}
      />
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {currentUser.email && (
        <QuoteHeader
          user={currentUser}
          onLogout={handleLogout}
          t={t}
          onOpenHistory={() => quoteHistory.setDrawerOpen(true)}
          onSave={handleSave}
          activeQuoteTitle={quoteHistory.activeQuote?.title}
          activeQuoteCustomer={quoteHistory.activeQuote?.customer_name}
          quoteStatus={quoteHistory.activeQuote?.status}
        />
      )}

      <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: "auto" }}>
        <BuildingPhase
          v5={v5}
          storeLogic={enhancedStoreLogic}
          categoryOptions={categoryOptions}
          catLoading={catLoading}
          t={t}
          quoteHistory={quoteHistory}
        />
      </Box>

      <QuoteHistoryDrawer
        open={quoteHistory.drawerOpen}
        onClose={() => quoteHistory.setDrawerOpen(false)}
        quotes={quoteHistory.quotes}
        activeQuoteId={quoteHistory.activeQuoteId}
        activeQuote={quoteHistory.activeQuote}
        loading={quoteHistory.loading}
        saving={quoteHistory.saving}
        onLoadQuote={quoteHistory.loadQuote}
        onCloneQuote={quoteHistory.cloneQuote}
        onDeleteQuote={quoteHistory.removeQuote}
        onNewQuote={quoteHistory.newQuote}
        onSaveQuote={quoteHistory.saveQuote}
        saveDialogOpen={quoteHistory.saveDialogOpen}
        onOpenSaveDialog={() => quoteHistory.setSaveDialogOpen(true)}
        onCloseSaveDialog={() => quoteHistory.setSaveDialogOpen(false)}
      />

    </Box>
  )
}

export default StorePageV5
